/* import __COLOCATED_TEMPLATE__ from './away-mode-banner.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type AdminAwayService from 'embercom/services/admin-away-service';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Snackbar from 'embercom/services/snackbar';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';

interface Args {}

interface Signature {
  Element: HTMLButtonElement;
  Args: Args;
}

export default class AwayModeBanner extends Component<Signature> {
  @service declare adminAwayService: AdminAwayService;
  @service declare snackbar: Snackbar;
  @service declare intl: IntlService;
  @service declare session: Session;
  @tracked showSuccessBanner = false;

  get shouldDisplayBanner() {
    return !this.adminAwayService.isAutoChanged && this.adminAwayService.reassignConversations;
  }

  get shouldDisplayAutoAwayBanner() {
    return (
      this.session.workspace.isFeatureEnabled('team-sops-teammate-status-auto-away-mode') &&
      this.adminAwayService.isAutoChanged &&
      this.adminAwayService.awayModeEnabled
    );
  }

  get shouldDisplaySuccessBanner() {
    return this.showSuccessBanner;
  }

  get bannerText() {
    if (this.session.workspace.isFeatureEnabled('team-sops-wlm-stop-email-reassignment')) {
      return this.intl.t('inbox.away-mode-banner.turn-off-away-mode-and-stop-reassigning-chat');
    }

    return this.intl.t('inbox.away-mode-banner.turn-off-away-mode-and-stop-reassigning');
  }

  get autoAwayBannerText() {
    if (this.adminAwayService.reassignConversations) {
      return this.intl.t('inbox.away-mode-banner.auto-away-and-reassigning-mode', {
        time: this.formatDate(this.adminAwayService.autoChangedTime),
        htmlSafe: true,
      });
    }
    return this.intl.t('inbox.away-mode-banner.auto-away-mode', {
      time: this.formatDate(this.adminAwayService.autoChangedTime),
      htmlSafe: true,
    });
  }

  get awayToActiveSuccessText() {
    return this.intl.t('inbox.away-mode-banner.auto-away-to-active-success');
  }

  formatDate(date: any) {
    if (!date) {
      return '';
    }
    return this.intl.formatDate(date, {
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  @action setToActive() {
    this.snackbar.notify(this.intl.t('inbox.away-mode-disabled'));
    this.adminAwayService.toggleAwayMode();
  }

  @action setAutoAwayToActive() {
    this.adminAwayService.toggleAwayMode();
    this.showSuccessBanner = true;
    setTimeout(() => {
      this.showSuccessBanner = false;
    }, 3000);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::AwayModeBanner': typeof AwayModeBanner;
  }
}
